import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema.d.ts';

export type ClientPlatform = 'web' | 'ios_pwa' | 'android';

export interface ApiClientOptions {
  /** Base URL of the API including /v1 (default: same-origin '/api/v1' behind the Caddy proxy). */
  baseUrl?: string;
  platform?: ClientPlatform;
  /** Bearer token getter for non-cookie clients (tests / native). */
  getToken?: () => string | null | Promise<string | null>;
  /** Called when a request is unauthenticated after a refresh attempt (redirect to login). */
  onUnauthenticated?: () => void;
  fetch?: typeof fetch;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Creates the typed API client. Cookies are sent automatically; mutations carry X-Requested-With for CSRF. */
export function createApiClient(opts: ApiClientOptions = {}) {
  const baseUrl = opts.baseUrl ?? '/api/v1';
  const platform = opts.platform ?? detectPlatform();
  const client = createClient<paths>({ baseUrl, credentials: 'include', fetch: opts.fetch });

  let refreshing: Promise<boolean> | null = null;
  const refresh = () =>
    (refreshing ??= (async () => {
      try {
        const res = await (opts.fetch ?? fetch)(`${baseUrl}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Client': platform, 'X-Requested-With': 'mosaferati', 'Content-Type': 'application/json' },
          body: '{}',
        });
        return res.ok;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })());

  const headers: Middleware = {
    async onRequest({ request }) {
      request.headers.set('X-Client', platform);
      request.headers.set('Accept', 'application/json');
      if (MUTATING.has(request.method)) request.headers.set('X-Requested-With', 'mosaferati');
      const token = await opts.getToken?.();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status !== 401 || request.url.includes('/auth/')) return response;
      if (request.headers.get('X-Retried') === '1') {
        opts.onUnauthenticated?.();
        return response;
      }
      const ok = await refresh();
      if (!ok) {
        opts.onUnauthenticated?.();
        return response;
      }
      const retry = new Request(request, { headers: new Headers(request.headers) });
      retry.headers.set('X-Retried', '1');
      return (opts.fetch ?? fetch)(retry);
    },
  };
  client.use(headers);
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;

function detectPlatform(): ClientPlatform {
  if (typeof navigator === 'undefined') return 'web';
  const nav = navigator as Navigator & { standalone?: boolean };
  const ios = /iP(hone|ad|od)/.test(navigator.userAgent);
  const standalone = nav.standalone === true || (typeof matchMedia !== 'undefined' && matchMedia('(display-mode: standalone)').matches);
  return ios && standalone ? 'ios_pwa' : 'web';
}

/** Problem+json shape returned by the API for all errors. */
export interface ApiProblem {
  status: number;
  code: string;
  title: string;
  detail?: string;
  type?: string;
  errors?: { location?: string; message?: string; value?: unknown }[];
}

export class ApiError extends Error {
  constructor(public readonly problem: ApiProblem, public readonly response?: Response) {
    super(problem.detail || problem.title || `HTTP ${problem.status}`);
    this.name = 'ApiError';
  }
  get status() { return this.problem.status; }
  get code() { return this.problem.code; }
  get retryAfter(): number | null {
    const v = this.response?.headers.get('Retry-After');
    return v ? Number(v) : null;
  }
}

/** Normalizes an openapi-fetch result into data-or-throw. */
export function unwrap<T>(res: { data?: T; error?: unknown; response: Response }): T {
  if (res.error !== undefined || !res.response.ok) {
    const p = (res.error ?? { status: res.response.status, code: 'unknown', title: res.response.statusText }) as ApiProblem;
    throw new ApiError(p, res.response);
  }
  return res.data as T;
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
